import React, { useEffect } from 'react'
import { ArrowLeft, Shield, FileText } from 'lucide-react'

interface TermsOfServiceProps {
  onBack?: () => void
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary-600/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-primary-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {onBack && (
            <button
              onClick={onBack}
              className="
                inline-flex items-center space-x-2 mb-6
                text-light-tertiary hover:text-light-primary
                transition-colors duration-200
                focus:outline-none focus:ring-2 focus:ring-primary-500/50 rounded-lg p-2
              "
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          )}
          
          <div className="flex items-center space-x-4 mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-500 rounded-xl shadow-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-light-primary">Terms of Service</h1>
              <p className="text-light-tertiary">BLP Assistant - Listening Glass, LLC</p>
            </div>
          </div>
          
          <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4">
            <p className="text-sm text-primary-300">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>

        {/* Terms Content */}
        <div className="
          bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60
          backdrop-blur-lg 
          border border-dark-tertiary/30 
          rounded-2xl 
          p-8 
          shadow-xl
          prose prose-invert max-w-none
        ">
          <div className="space-y-8 text-light-secondary">
            
            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4 flex items-center space-x-2">
                <Shield className="w-5 h-5 text-primary-400" />
                <span>1. Acceptance of Terms</span>
              </h2>
              <p className="mb-4">
                By accessing and using the BLP Assistant application ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this Service.
              </p>
              <p>
                This Service is operated by Listening Glass, LLC ("Company", "we", "us", or "our"). These Terms of Service ("Terms") govern your use of our BLP (Beneficiary Liquidity Plan) Assistant application.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">2. Description of Service</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The BLP Assistant is an AI-powered tool designed to assist professionals during BLP sales calls</li>
                <li>Provides information and support related to Beneficiary Liquidity Plans</li>
                <li>Offers contextual responses based on the Beneficiary Liquidity Plan (BLP)</li>
                <li>Includes user authentication and secure access features</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">3. User Account and Registration</h2>
              <div className="space-y-3">
                <p><strong>Account Creation:</strong> You must create an account to use certain features of the Service.</p>
                <p><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials.</p>
                <p><strong>Accurate Information:</strong> You agree to provide accurate, current, and complete information during registration.</p>
                <p><strong>Account Responsibility:</strong> You are responsible for all activities that occur under your account.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">4. Acceptable Use Policy</h2>
              <div className="space-y-4">
                <p><strong>Permitted Uses:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Professional BLP sales support</li>
                  <li>Educational purposes related to BLP products</li>
                  <li>Legitimate business communications</li>
                </ul>
                
                <p><strong>Prohibited Uses:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Attempting to compromise system security</li>
                  <li>Using the service for illegal activities</li>
                  <li>Sharing account credentials with unauthorized users</li>
                  <li>Attempting to reverse engineer or extract proprietary information</li>
                  <li>Using automated tools to access the service without permission</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">5. Intellectual Property Rights</h2>
              <div className="space-y-3">
                <p><strong>Company Content:</strong> All content, features, and functionality of the Service are owned by Listening Glass, LLC and are protected by copyright, trademark, and other laws.</p>
                <p><strong>BLP Trademark:</strong> The Beneficiary Liquidity Plan (BLP) is a registered trademark and is used with permission of Phil Graham, creator of the BLP.</p>
                <p><strong>User Content:</strong> You retain ownership of content you provide, but grant us a license to use it for providing the Service.</p>
                <p><strong>Restrictions:</strong> You may not copy, modify, distribute, or create derivative works from our content without explicit permission.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">6. Privacy and Data Protection</h2>
              <p>
                Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to the collection and use of information as outlined in our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">7. SMS Notifications</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Service Description</h3>
                  <p className="mb-3">
                    The Service may offer optional SMS notifications for administrators to receive operational alerts about customer account events. These notifications are transactional and non-marketing in nature.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Terms of SMS Service</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Opt-In Required:</strong> You must explicitly opt-in by providing your phone number in the admin dashboard</li>
                    <li><strong>Message Frequency:</strong> Message frequency varies based on customer account activity (typically low volume)</li>
                    <li><strong>Message Types:</strong> Notifications include trial starts, subscription payments, and cancellations</li>
                    <li><strong>Costs:</strong> Standard message and data rates may apply from your mobile carrier</li>
                    <li><strong>Supported Carriers:</strong> Service works with most major US carriers (AT&T, T-Mobile, Verizon, etc.)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">User Control and Opt-Out</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Reply <strong>STOP</strong> to any message to unsubscribe from all SMS notifications</li>
                    <li>Reply <strong>HELP</strong> for assistance with SMS notifications</li>
                    <li>You can manage SMS preferences in the admin dashboard at any time</li>
                    <li>SMS consent is not a condition of purchase or use of other Service features</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-light-primary mb-2">Privacy and Data Protection</h3>
                  <p className="mb-2">
                    By opting into SMS notifications, you acknowledge and agree that:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Your mobile phone number will be securely stored and used solely for sending operational notifications</li>
                    <li>Your phone number will <strong>NOT be sold, shared with third parties for promotional purposes, or used for marketing</strong></li>
                    <li>SMS notifications are delivered via Telnyx, a trusted third-party telecommunications provider</li>
                    <li>You can withdraw consent at any time by replying STOP or updating your preferences</li>
                  </ul>
                </div>

                <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4">
                  <p className="text-sm text-primary-300">
                    <strong>Support:</strong> For questions about SMS notifications, contact us through the channels provided in the application or visit our Privacy Policy for more information.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">8. Service Availability and Modifications</h2>
              <div className="space-y-3">
                <p><strong>Availability:</strong> We strive to maintain Service availability but do not guarantee uninterrupted access.</p>
                <p><strong>Modifications:</strong> We reserve the right to modify, suspend, or discontinue the Service at any time.</p>
                <p><strong>Updates:</strong> We may update these Terms periodically. Continued use constitutes acceptance of updated Terms.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">9. Limitation of Liability</h2>
              <div className="space-y-3">
                <p><strong>Service Basis:</strong> The Service is provided "as is" and "as available" without warranties of any kind.</p>
                <p><strong>Professional Advice:</strong> The Service provides informational support only and does not constitute professional insurance advice.</p>
                <p><strong>Liability Limits:</strong> Listening Glass, LLC shall not be liable for any indirect, incidental, special, or consequential damages.</p>
                <p><strong>Maximum Liability:</strong> Our total liability shall not exceed the amount paid by you for the Service in the past 12 months.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">10. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless Listening Glass, LLC from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">11. Termination</h2>
              <div className="space-y-3">
                <p><strong>Termination Rights:</strong> Either party may terminate the agreement at any time.</p>
                <p><strong>Effect of Termination:</strong> Upon termination, your right to use the Service ceases immediately.</p>
                <p><strong>Data Retention:</strong> We may retain certain information as required by law or for legitimate business purposes.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">12. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the state of Ohio where Listening Glass, LLC is established, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">13. Contact Information</h2>
              <div className="bg-dark-secondary/50 rounded-lg p-4 border border-dark-tertiary/30">
                <p className="mb-2"><strong>Listening Glass, LLC</strong></p>
                <p className="text-sm text-light-tertiary">
                  For questions regarding these Terms of Service, please contact us through the appropriate channels provided in the application.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">14. Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that the remaining Terms will otherwise remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-light-primary mb-4">15. Entire Agreement</h2>
              <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Listening Glass, LLC regarding the use of the Service and supersede all prior agreements and understandings.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  )
}